<template>
  <el-card
    v-loading="isLoading"
    shadow="never"
  >
    <template slot="header">
      <div class="d-flex align-items-center justify-content-between">
        <span>
          Search by:
        </span>

        <div class="sort-control">
          <el-popover
            v-if="hasSortables"
            ref="sortPopover"
            placement="bottom-end"
            width="208"
            trigger="click"
            popper-class="simplified-sort-popper"
            @show="syncSortDraft"
          >
            <div class="sort-panel">
              <p class="sort-panel__title">
                Sort by
              </p>

              <el-radio-group
                class="sort-panel__fields"
                size="mini"
                :value="draftSortField"
                @input="setSortField"
              >
                <el-radio
                  v-for="sortable in sortables"
                  :key="sortable.field"
                  :label="sortable.field"
                  class="sort-panel__option"
                >
                  {{ sortable.label }}
                </el-radio>
              </el-radio-group>

              <el-divider class="sort-panel__divider" />

              <p class="sort-panel__title">
                Direction
              </p>

              <el-radio-group
                class="sort-panel__direction"
                size="mini"
                :value="draftSortDirection"
                @input="setSortDirection"
              >
                <el-radio label="asc">
                  Ascending
                </el-radio>
                <el-radio label="desc">
                  Descending
                </el-radio>
              </el-radio-group>

              <br>

              <div class="sort-panel__actions">
                <el-button
                  size="mini"
                  @click.native="resetSort"
                >
                  Reset
                </el-button>
                <el-button
                  type="primary"
                  size="mini"
                  @click.native="applySort"
                >
                  Apply
                </el-button>
              </div>
            </div>

            <span
              slot="reference"
              class="sort-trigger"
            >
              <i class="el-icon-sort sort-trigger__icon" />
              {{ activeSortLabel }}
              <i class="el-icon-arrow-down el-icon--right" />
            </span>
          </el-popover>
        </div>
      </div>
    </template>
    <template v-if="!!$scopedSlots['before']">
      <slot name="before" />
      <el-divider />
    </template>

    <template v-if="hasFilterables">
      <FilterRow
        v-for="(filterable, index) in visibleFilterables"
        ref="filterRow"
        :key="filterable.id"
        :class="{ 'mb-2': index + 1 !== visibleFilterables.length }"
        :resource="resource"
        :filterable="filterable"
        :advanced="isAdvancedFilter"
        @keydown.enter="$event.preventDefault()"
        @apply="apply"
      >
        <template v-slot:label="{ resource, filterable }">
          <slot
            :name="`${filterable.field}.label`"
            :resource="resource"
            :filterable="filterable"
          />
        </template>

        <template v-slot:operator="{ resource, filterable }">
          <slot
            :name="`${filterable.field}.operator`"
            :resource="resource"
            :filterable="filterable"
          />
        </template>

        <template v-slot:input="{ resource, filterable }">
          <slot
            :name="`${filterable.field}.input`"
            :resource="resource"
            :filterable="filterable"
            :apply="apply"
          />
        </template>
      </FilterRow>

      <el-tag
        v-if="hasInvisibleFilterables"
        class="mt-3 mb-1 cursor-pointer"
        type="info"
        size="mini"
        @click.native="toggleFilterableVisibility"
      >
        + {{ invisibleFilterables.length }} more
      </el-tag>

      <div class="pt-3 text-right">
        <el-button
          size="mini"
          @click.native="reset"
        >
          Reset All
        </el-button>

        <el-button
          type="primary"
          size="mini"
          @click.native="apply"
        >
          Apply
        </el-button>
      </div>

      <div class="clearfix" />
    </template>

    <p
      v-else
      class="w-100 text-center text-muted"
    >
      No filters available
    </p>
  </el-card>
</template>

<script>
/* eslint-disable */
import FilterRow from '@/components/Filters/Simplified/Filter'
import Resource from '@/plugins/sdk/Resource'
import { mapMutations, mapGetters } from 'vuex'
import { SET_FILTERS, CLEAR_FILTERS } from '@/store/mutation-types'
import Store from '@/store'

export default {
  components: {
    FilterRow
  },
  props: {
    value: {
      type: Array,
      required: false,
      default: () => ([])
    },
    resource: {
      type: [ Object, Function ],
      required: true,
      validator: function (value) {
        return value instanceof Resource || value.prototype instanceof Resource
      }
    },
    advanced: {
      type: Boolean,
      required: false,
      default: false
    },
    primaryFields: {
      type: Array,
      required: false,
      default: () => ([])
    },
    hiddenFields: {
      type: Array,
      required: false,
      default: () => ([])
    },
    setupFields: {
      type: [ Array, Object ],
      required: false,
      default: () => ({})
    },
    test: {
      type: Array,
      required: false,
      default: () => ([])
    },
    context: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      isLoading: true,
      isShowingAllFilterables: false,
      // Filterables are the available filters
      // for the resource.
      filterables: [],
      sortables: [],
      sortDirection: 'asc',
      sortField: null,
      draftSortDirection: 'asc',
      draftSortField: null
    }
  },
  computed: {
    ...mapGetters({
      wantsAdvancedFilters: 'configuration/search/isAdvancedFilter',
      wantsExperimentalFilter: 'configuration/search/isExperimentalFilter',
      getFilters: 'filter/filters',
      storeFilters: 'configuration/search/storeFilters'
    }),
    isAdvancedFilter () {
      return this.advanced || this.wantsAdvancedFilters
    },
    /**
     * Returns visible filterables.
     *
     * @return     {Array}
     */
    visibleFilterables () {
      return this
        .$_
        .filter(this.filterables, filterable => {
          return (
            !this.hiddenFields.includes(filterable.field) &&
            (
              // a filterable is always visible when showing all filterables
              this.isShowingAllFilterables ||
              // a filterable is considered visible if they are declared
              // as primary filterable fields
              this.primaryFields.includes(filterable.field) ||
              // a filterable is considered visible if it is active
              filterable.filter.isValid
            )
          )
        })
    },
    /**
     * Returns invisible filterables.
     *
     * @return     {Array}
     */
    invisibleFilterables () {
      return this.$_.filter(this.filterables, filterable => {
        return !this.hiddenFields.includes(filterable.field) && !this.$_.find(this.visibleFilterables, { id: filterable.id })
      })
    },
    /**
     * Determines if filterables are available for the resource.
     *
     * @return     {boolean}  True if has filterables, False otherwise.
     */
    hasFilterables () {
      return this.filterables.length > 0
    },
    /**
     * Determines if there are non visible filterables.
     *
     * @return     {boolean}  True if has invisible filterables, False otherwise.
     */
    hasInvisibleFilterables () {
      return this.invisibleFilterables.length > 0
    },
    hasSortables () {
      return this.sortables.length > 0
    },
    activeSortLabel () {
      if (!this.sortField) {
        return 'Sort'
      }

      const sortable = this.sortables.find(item => item.field === this.sortField)
      if (!sortable) {
        return 'Sort'
      }

      return `${sortable.label} (${this.sortDirection === 'asc' ? 'Asc' : 'Desc'})`
    }
  },
  async created () {
    // When the filters component is created obtain all
    // filters available for the resource
    this.filterables = await this
      .$sdk
      .filter
      .list(request => {
        request.filters = { resource: `${this.context ? this.context + '_' : ''}${this.resource}` }
      })

    // Check for filterable setup hooks
    this.setup()
    this.setSortables()
    this.update()

    this.$nextTick(() => {
      this.isLoading = false
      this.$emit('ready')
      this.tryFocusFirstInput()
    })
  },
  methods: {
    tryFocusFirstInput () {
      if (this.$route.name !== 'freight-consignments-v3-list') {
        return
      }

      this.$nextTick(() => {
        const targetIndex = this.visibleFilterables.findIndex(filterable => filterable.field === 'con_full')
        if (targetIndex < 0) {
          return
        }

        const rows = Array.isArray(this.$refs.filterRow) ? this.$refs.filterRow : [this.$refs.filterRow]
        // console.table(rows)
        const targetRow = rows[targetIndex]
        if (!targetRow || !targetRow.$el) {
          console.warn('Target filter row not found for focusing')
          return
        }

        const targetInput = targetRow.$refs.textInput || targetRow.$el.querySelector('input')
        if (targetInput) {
          targetInput.focus()
        }
      })
    },
    ...mapMutations({
      setFilters: `filter/${SET_FILTERS}`
    }),
    toggleFilterableVisibility () {
      this.isShowingAllFilterables = !this.isShowingAllFilterables
    },
    setup () {
      this.filterables.map(filterable => {
        if (Array.isArray(this.setupFields) && this.setupFields.includes(filterable.field)) {
          this.$emit('setup', filterable.field, filterable)
        } else if (this.$_.isPlainObject(this.setupFields) && typeof this.setupFields[filterable.field] === 'function') {
          this.setupFields[filterable.field](filterable)
        }
      })
      // Load stored filters
      if (this.storeFilters && this.getFilters(this.$route.fullPath)) {
        this.getFilters(this.$route.fullPath).map(filter => {
          // this.$emit('setup', filter.field, filter)
          const filterable = this.filterables.find(f => f.field === filter.field)
          if (filterable) {
            filterable.filter.value = filter.value
            filterable.filter.operator = filter.operator
            filterable.filter.label = this.getLabelForObject(filterable, filter.value)
            // this.$emit('updateFilters', filterable.filter)
          }
        })
      }
    },
    setSortables () {
      if (this.resource && this.resource.sortables) {
        this.sortables = this.resource.sortables
      } else {
        this.sortables = []
      }
    },
    getLabelForObject (filterable, value) {
      if (value === null || value === undefined) {
        return undefined
      }
      // if value is array and value[0].constructor.name is a this.$sdk.resource, instanciate and get label
      if (Array.isArray(value) && value.length > 0) {
        let labels = []
        const firstValue = value[0]
        if (!this.$sdk.checkResource(firstValue.constructor.name)) {
          return value.toString()
        }
        const resourceClass = new this.$sdk[firstValue.constructor.name]
        if (resourceClass) {
          resourceClass.fill(firstValue)
          labels.push(resourceClass.label)
        }
        return labels
      }

      return value.toString()
    },
    reset () {
      this.isLoading = true

      Store.commit(`filter/${CLEAR_FILTERS}`, this.$route.fullPath)

      this
        .filterables
        .map(filterable => {
          filterable.filter.value = undefined
        })

      this.setup()

      this.$nextTick(() => {
        this.isLoading = false
        this.apply()
      })
    },
    apply () {
      this.update()

      this.$nextTick(() => {
        this.$emit('apply')
      })
    },
    update () {
      const filters = []

      this
        .filterables
        .map(filterable => {
          if (filterable.filter.isValid) {
            filters.push(filterable.filter)
          }
        })

      if (this.storeFilters && filters.length !== 0) {
        // If there are no filters, clear any stored filters
        filters.map(filter => {
          console.log(filter.label)
        })
        this.setFilters({ routePath: this.$route.fullPath, filters: filters })
      }


      this.$emit('input', filters)
    },
    setSortField (field) {
      this.draftSortField = field
    },
    setSortDirection (direction) {
      this.draftSortDirection = direction
    },
    syncSortDraft () {
      this.draftSortField = this.sortField
      this.draftSortDirection = this.sortDirection
    },
    applySort () {
      this.sortField = this.draftSortField
      this.sortDirection = this.draftSortDirection
      this.$emit('sort', { field: this.sortField, direction: this.sortDirection })
      this.$refs.sortPopover.doClose()
    },
    resetSort () {
      this.draftSortField = null
      this.draftSortDirection = 'asc'
      this.sortField = null
      this.sortDirection = 'asc'
      this.$emit('sort', { field: this.sortField, direction: this.sortDirection })
    }
  }
}
</script>

<style scoped>
.sort-control {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  float: right;
}

.sort-trigger {
  display: inline-flex;
  align-items: center;
  color: #606266;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.sort-trigger__icon {
  margin-right: 4px;
}

.sort-panel__title {
  color: #606266;
  font-size: 11px;
  font-weight: 600;
  margin: 0 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sort-panel__fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sort-panel__option {
  line-height: 1.4;
  margin-right: 0;
}

.sort-panel__divider {
  margin: 10px 0;
}

.sort-panel__direction {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sort-panel__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

:deep(.simplified-sort-popper) {
  padding: 8px 10px;
}
</style>
